import { createTheme } from '@mui/material/styles';
import { colors, semanticColors } from './colors';

/**
 * TGAS ERP Theme Configuration
 *
 * Brand Colors: #302884 (Blue, primary), #E30613 (Red) - sampled from the logo
 * Primary UI: Brand Blue
 *
 * Professional, accessible color palette with brand identity
 * Fully responsive WCAG 2.1 AA compliant design system
 */
export const theme = createTheme({
  palette: {
    mode: 'light',

    // Semantic colors
    primary: semanticColors.primary,        // Brand Blue #302884 (Main UI)
    secondary: semanticColors.secondary,    // Brand Red #CC3433 (Actions)
    success: semanticColors.success,        // Green (Positive)
    warning: semanticColors.warning,        // Amber (Warnings)
    error: semanticColors.error,            // Brand Red #CC3433 (Errors)
    info: semanticColors.info,              // Blue (Info)

    background: semanticColors.background,
    text: semanticColors.text,
    divider: semanticColors.divider,
    action: semanticColors.action,
  },

  // Typography - Professional government style
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),

    // Consistent font sizes
    h1: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.2 },
    h2: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    button: {
      textTransform: 'none', // No uppercase buttons for better readability
      fontWeight: 500,
    },
    caption: { fontSize: '0.75rem', lineHeight: 1.4 },
  },

  // Shape - Consistent border radius
  shape: {
    borderRadius: 8,
  },

  // Spacing - 8px base unit
  spacing: 8,

  // Component overrides for brand consistency
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        sizeLarge: {
          padding: '10px 20px',
          fontSize: '1rem',
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: '0.875rem',
        },
        // Primary button uses brand blue
        containedPrimary: {
          backgroundColor: colors.brandBlue.base,
          '&:hover': {
            backgroundColor: colors.brandBlue.dark,
          },
        },
        // Secondary button uses brand red
        containedSecondary: {
          backgroundColor: colors.brandRed.base,
          '&:hover': {
            backgroundColor: colors.brandRed.dark,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          border: `1px solid ${colors.neutral[200]}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default gradient
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
        elevation2: {
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${colors.neutral[200]}`,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: semanticColors.error.main,
          fontWeight: 700,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.neutral[200]}`,
        },
        head: {
          fontWeight: 600,
          backgroundColor: colors.neutral[50],
        },
      },
    },
  },
});

// Export colors for direct use when needed (rare cases)
export { colors, semanticColors };
